import React from 'react';

import { IDENTITY_GLYPH_COLORS, IDENTITY_HUES } from '../../config/brand';
import { CHECK } from '../icons';
import { identityGlyphColor } from '../../utils/colors';

export const sameColor = (a, b) => `${a || ''}`.trim().toLowerCase() === `${b || ''}`.trim().toLowerCase();

/**
 * The 24 identity hues and the Custom swatch as one row of buttons: the
 * palette of the icon + color modal (IconColorPicker.js) and of the `color`
 * element's popup (ColorInputPanel.js), written once so the two cannot drift
 * (KPR-325; the palette itself is KPR-313's IDENTITY_HUES).
 *
 * `onPick(hex)` for a hue; `onToggleCustom()` for the Custom swatch, which is
 * "pressed" only when the current color is NOT one of the hues, so a palette
 * pick never reads as two selected swatches. The selected mark is a check, not
 * a ring alone: a ring is pink on a pink swatch.
 */
export default function ColorPalette({ color, onPick, customOpen, onToggleCustom, customPanelId, className = '' }) {
	const customPicked = Boolean(color && !IDENTITY_HUES.some(h => sameColor(h.value, color)));
	return (
		<div className={`icon-picker__hues ${className}`.trim()} role='group' aria-label='Color'>
			{IDENTITY_HUES.map(hue => {
				const picked = sameColor(hue.value, color);
				return (
					<button
						key={hue.key}
						type='button'
						className={`icon-picker__hue${picked ? ' is-selected' : ''}`}
						style={{ '--hue': hue.value, '--hue-glyph': IDENTITY_GLYPH_COLORS[hue.glyph] }}
						aria-label={hue.label}
						aria-pressed={picked}
						title={hue.label}
						data-tone={hue.tone}
						onClick={() => onPick(hue.value)}
					>
						{picked ? <CHECK aria-hidden='true' className='icon-picker__hue-check' data-testid='hue-check' /> : null}
					</button>
				);
			})}
			<button
				type='button'
				className={`icon-picker__hue icon-picker__hue--custom${customPicked ? ' is-selected' : ''}`}
				// Painted only when the color IS custom: with a palette hue picked it
				// used to wear that hue too and read as a second selected swatch.
				style={{ '--hue': customPicked ? color : 'transparent', '--hue-glyph': customPicked ? identityGlyphColor(color) : undefined }}
				aria-label='Custom color'
				aria-pressed={customPicked}
				aria-expanded={customOpen}
				aria-controls={customOpen ? customPanelId : undefined}
				title='Custom color'
				onClick={onToggleCustom}
			>
				{customPicked
					? <CHECK aria-hidden='true' className='icon-picker__hue-check' data-testid='hue-check' />
					: <span aria-hidden='true'>…</span>}
			</button>
		</div>
	);
}
